<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catBlue" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-sleep" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="latest && latest.quality"
        x-small
        :color="qualityColor(latest.quality)"
        text-color="white"
        class="ml-2 flex-shrink-0"
      >
        {{ qualityLabel(latest.quality) }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar avaliação de sono</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!latest" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-sleep-off</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma avaliação de sono registrada.
        </p>
        <v-btn small color="catBlue" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Registrar sono
        </v-btn>
      </div>

      <!-- Avaliação atual -->
      <div v-else class="info-list">
        <div class="info-row">
          <span class="info-label">Qualidade</span>
          <span class="info-value">{{ qualityLabel(latest.quality) || "—" }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Rotina na semana</span>
          <span class="info-value">{{ boolLabel(latest.has_weekday_routine) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Rotina no fim de semana</span>
          <span class="info-value">{{ weekendLabel(latest.weekend_routine) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Apneia do sono</span>
          <span class="info-value">{{ boolLabel(latest.has_apnea) }}</span>
        </div>
        <div v-if="latest.notes" class="info-row">
          <span class="info-label">Observação</span>
          <span class="info-value">{{ latest.notes }}</span>
        </div>
      </div>
    </v-card-text>

    <EditSleepDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :records="records"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getSleepByPatient } from "@/services/sleepService";
import EditSleepDialog from "@/features/patients/profile-dashboard/dialogs/EditSleepDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "SleepCard",

  components: {
    EditSleepDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      records: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.records || []).length;
    },
    latest() {
      return this.records.length ? this.records[0] : null;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.records = await dashboardStore.section(this.patientId, "sleep", () => getSleepByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar avaliação de sono:", error);
        this.records = [];
      }
    },

    qualityLabel(q) {
      return (
        { bom: "Bom", irregular: "Irregular", ruim: "Ruim", insonia: "Insônia" }[q] ||
        ""
      );
    },

    qualityColor(q) {
      return (
        {
          bom: "green darken-1",
          irregular: "amber darken-2",
          ruim: "deep-orange darken-1",
          insonia: "red darken-2",
        }[q] || "grey"
      );
    },

    boolLabel(v) {
      if (v === true || v === 1) return "Sim";
      if (v === false || v === 0) return "Não";
      return "—";
    },

    weekendLabel(v) {
      return { sim: "Sim", nao: "Não", as_vezes: "Às vezes" }[v] || "—";
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Azul-noite (Sono) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #bfdbfe !important;
  box-shadow: 0 2px 10px rgba(29, 78, 216, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #93c5fd !important;
  box-shadow: 0 6px 20px rgba(29, 78, 216, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-bottom: 1px solid #bfdbfe;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-blue);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #93c5fd;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-cat-blue);
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  border-bottom: 1px dashed #dbeafe;
  padding-bottom: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-cat-blue);
  flex-shrink: 0;
}

.info-value {
  font-size: 13px;
  color: var(--ds-text-600);
  text-align: right;
}
</style>
